import React, { useState } from 'react';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { CallbackModal } from '../../components/CallbackModal';
import { Calendar, Clock, Tag, ChevronLeft, MessageCircle, Plus, Minus, CheckCircle2, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-856">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-857">
        <span className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-858">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-859">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-860" /> : <Plus className="w-4 h-4" data-id="element-861" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-862">
        <p className="text-gray-600 leading-relaxed" data-id="element-863">{answer}</p>
      </div>
    </div>;
}
export function VeneersGuideArticle() {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Чи псують вініри зуби?',
    a: "Ні, якщо процедура виконана правильно. Ми використовуємо мінімально інвазивні техніки, знімаючи лише тонкий шар емалі (0.3-0.5 мм), щоб зберегти здоров'я зуба."
  }, {
    q: 'Скільки коштують вініри в Лондоні?',
    a: 'Композитні вініри коштують від £250-£400 за зуб, керамічні — від £700-£1000. Ціна залежить від матеріалу та складності.'
  }, {
    q: 'Чи можна їсти все з вінірами?',
    a: 'Так, але варто уникати дуже твердої їжі (горіхи, лід) та не гризти предмети, щоб уникнути сколів.'
  }, {
    q: 'Скільки служать вініри?',
    a: 'Керамічні вініри — 10-15 років і більше. Композитні — 5-7 років і потребують періодичного полірування.'
  }, {
    q: 'Чи можна відбілити вініри?',
    a: 'Ні, кераміка не змінює колір. Якщо ви хочете білішу посмішку, спочатку потрібно відбілити свої зуби, а потім підбирати колір вінірів.'
  }];
  // Schemas
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: 'Вініри в Лондоні — все, що потрібно знати перед встановленням',
    datePublished: '2026-02-12',
    author: {
      '@type': 'Organization',
      name: 'Dentico Clinic'
    },
    publisher: {
      '@type': 'Organization',
      name: 'Dentico Clinic',
      logo: {
        '@type': 'ImageObject',
        url: 'https://denticoclinic.co.uk/logo.png'
      }
    },
    description: 'Повний гід по вінірах: типи, ціни, процес встановлення та догляд. Дізнайтеся, як отримати ідеальну посмішку в Лондоні.',
    inLanguage: 'uk'
  };
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  return <Layout data-id="element-864">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-865" />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(articleSchema)
    }} data-id="element-866" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-867" />

      <article className="bg-white" data-id="element-868">
        {/* Header */}
        <div className="bg-teal-50 py-12 lg:py-20" data-id="element-869">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-870">
            <Link to="/ua/blog" className="inline-flex items-center text-teal-600 font-medium mb-6 hover:underline" data-id="element-871">
              <ChevronLeft className="w-4 h-4 mr-1" data-id="element-872" />
              Назад до блогу
            </Link>

            <div className="flex flex-wrap gap-4 mb-6 text-sm text-gray-600" data-id="element-873">
              <span className="bg-white px-3 py-1 rounded-full font-medium text-teal-700 flex items-center shadow-sm" data-id="element-874">
                <Tag className="w-3 h-3 mr-2" data-id="element-875" />
                Естетика
              </span>
              <span className="flex items-center" data-id="element-876">
                <Calendar className="w-4 h-4 mr-2" data-id="element-877" />
                Лютий 2026
              </span>
              <span className="flex items-center" data-id="element-878">
                <Clock className="w-4 h-4 mr-2" data-id="element-879" />9 хв читання
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6" data-id="element-880">
              Вініри в Лондоні — все, що потрібно знати перед встановленням
            </h1>

            <div className="flex items-center" data-id="element-881">
              <div className="w-10 h-10 bg-teal-200 rounded-full flex items-center justify-center text-teal-700 font-bold mr-3" data-id="element-882">
                DC
              </div>
              <div data-id="element-883">
                <p className="font-medium text-gray-900" data-id="element-884">Dentico Clinic Team</p>
                <p className="text-sm text-gray-500" data-id="element-885">Експертна стаття</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-886">
          <div className="prose prose-lg prose-teal max-w-none text-gray-600" data-id="element-887">
            <p className="lead text-xl text-gray-800 font-medium mb-8" data-id="element-888">
              Ідеальна посмішка — це мрія багатьох. Вініри дозволяють змінити
              форму, колір та положення зубів за лічені тижні. Але чи підходять
              вони вам? Які обрати — керамічні чи композитні? У цьому гіді ми
              відповімо на всі питання.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-889">
              Що таке вініри?
            </h2>
            <p className="mb-6" data-id="element-890">
              Вініри — це тонкі накладки (схожі на контактні лінзи), які
              фіксуються на передню поверхню зубів. Вони маскують недоліки:
              сколи, плями, проміжки (діастеми) або незначні викривлення.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-891">
              Типи вінірів: Порцеляна vs Композит
            </h2>
            <p className="mb-6" data-id="element-892">
              Це найважливіший вибір, який вам доведеться зробити.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8" data-id="element-893">
              <div className="bg-gray-50 p-6 rounded-xl" data-id="element-894">
                <h3 className="text-xl font-bold text-teal-700 mb-4 flex items-center" data-id="element-895">
                  <Sparkles className="w-5 h-5 mr-2" data-id="element-896" />
                  Керамічні (Порцелянові)
                </h3>
                <ul className="space-y-2 text-sm" data-id="element-897">
                  <li data-id="element-898">
                    ✅ Виглядають максимально природно (пропускають світло).
                  </li>
                  <li data-id="element-899">
                    ✅ Не змінюють колір з часом (не фарбуються кавою/вином).
                  </li>
                  <li data-id="element-900">✅ Дуже міцні та довговічні (10-15 років).</li>
                  <li data-id="element-901">❌ Дорожчі.</li>
                  <li data-id="element-902">
                    ❌ Потребують 2-3 візити та виготовлення в лабораторії.
                  </li>
                </ul>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl" data-id="element-903">
                <h3 className="text-xl font-bold text-teal-700 mb-4 flex items-center" data-id="element-904">
                  <Sparkles className="w-5 h-5 mr-2" data-id="element-905" />
                  Композитні (Bonding)
                </h3>
                <ul className="space-y-2 text-sm" data-id="element-906">
                  <li data-id="element-907">✅ Дешевші.</li>
                  <li data-id="element-908">✅ Встановлюються за 1 візит (прямо в кріслі).</li>
                  <li data-id="element-909">✅ Легко ремонтуються при сколах.</li>
                  <li data-id="element-910">❌ Менш довговічні (5-7 років).</li>
                  <li data-id="element-911">❌ Можуть тьмяніти і потребують полірування.</li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-912">
              Процес встановлення (Кераміка)
            </h2>
            <ol className="list-decimal pl-6 space-y-4 mb-8" data-id="element-913">
              <li data-id="element-914">
                <strong data-id="element-915">Консультація та дизайн:</strong> Ми робимо фото та
                цифрове моделювання вашої майбутньої посмішки (Digital Smile
                Design).
              </li>
              <li data-id="element-916">
                <strong data-id="element-917">Mock-up (Примірка):</strong> Ми переносимо дизайн на
                ваші зуби за допомогою тимчасового матеріалу, щоб ви побачили
                результат "вживу" ще до початку робіт.
              </li>
              <li data-id="element-918">
                <strong data-id="element-919">Підготовка:</strong> Мінімальне обточування емалі (якщо
                потрібно) та зняття відбитків. Встановлення тимчасових вінірів.
              </li>
              <li data-id="element-920">
                <strong data-id="element-921">Фіксація:</strong> Через 1-2 тижні ми приміряємо та
                фіксуємо готові керамічні вініри.
              </li>
            </ol>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-922">
              Кому підходять вініри?
            </h2>
            <p className="mb-6" data-id="element-923">Вініри — ідеальне рішення, якщо у вас:</p>
            <ul className="space-y-2 mb-8" data-id="element-924">
              <li className="flex items-start" data-id="element-925">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-2 mt-1 flex-shrink-0" data-id="element-926" />
                <span data-id="element-927">Зуби змінили колір і не відбілюються.</span>
              </li>
              <li className="flex items-start" data-id="element-928">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-2 mt-1 flex-shrink-0" data-id="element-929" />
                <span data-id="element-930">Є сколи або тріщини.</span>
              </li>
              <li className="flex items-start" data-id="element-931">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-2 mt-1 flex-shrink-0" data-id="element-932" />
                <span data-id="element-933">Є проміжки між зубами.</span>
              </li>
              <li className="flex items-start" data-id="element-934">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-2 mt-1 flex-shrink-0" data-id="element-935" />
                <span data-id="element-936">Зуби мають неправильну форму.</span>
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-937">
              Чому Dentico Clinic?
            </h2>
            <p className="mb-6" data-id="element-938">
              Ми спеціалізуємося на естетичній стоматології. Наша мета —
              створити посмішку, яка виглядає <strong data-id="element-939">натурально</strong>, а не
              як "штучні білі зуби". Ми враховуємо пропорції вашого обличчя,
              міміку та побажання.
            </p>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 pt-10 border-t border-gray-100" data-id="element-940">
            <h3 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-941">
              Часті запитання
            </h3>
            <div className="space-y-2" data-id="element-942">
              {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-943" />)}
            </div>
          </div>

          {/* Read Also */}
          <div className="mt-16" data-id="element-944">
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-945">
              Читайте також
            </h3>
            <div className="grid sm:grid-cols-2 gap-4" data-id="element-946">
              <Link to="/ua/veneers-london" data-id="element-947">
                <Card className="p-4 hover:shadow-md transition-shadow h-full flex items-center" data-id="element-948">
                  <span className="text-teal-600 font-medium" data-id="element-949">
                    Вініри в Dentico Clinic
                  </span>
                </Card>
              </Link>
              <Link to="/ua/services/smile-makeover" data-id="element-950">
                <Card className="p-4 hover:shadow-md transition-shadow h-full flex items-center" data-id="element-951">
                  <span className="text-teal-600 font-medium" data-id="element-952">
                    Smile Makeover
                  </span>
                </Card>
              </Link>
            </div>
          </div>
        </div>

        {/* CTA */}
        <section className="py-16 bg-teal-900" data-id="element-953">
          <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-954">
            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-955">
              Час для вашої ідеальної посмішки
            </h2>
            <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto" data-id="element-956">
              Запишіться на консультацію та примірку майбутньої посмішки.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-957">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-958">
                <Button size="lg" className="w-full gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-959">
                  <Calendar className="w-5 h-5" data-id="element-960" />
                  Записатись онлайн
                </Button>
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-961">
                <Button variant="outline" size="lg" className="w-full gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" data-id="element-962">
                  <MessageCircle className="w-5 h-5" data-id="element-963" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </section>
      </article>
    </Layout>;
}